import { useState, useEffect } from 'react';
import { FlowNode } from '../../types';
import { JsonTextarea } from './JsonTextarea';

type Mode = 'fields' | 'raw';

interface Field {
  id: string;
  key: string;
  value: string;
}

function newField(): Field {
  return { id: crypto.randomUUID(), key: '', value: '' };
}

// label/alias are step metadata, not output fields.
const RESERVED_KEYS = ['label', 'alias'];

function outputEntries(config: Record<string, unknown> | undefined): [string, unknown][] {
  return Object.entries(config ?? {}).filter(([k]) => !RESERVED_KEYS.includes(k));
}

function isNested(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

function entriesToFields(entries: [string, unknown][]): Field[] {
  return entries.length > 0
    ? entries.map(([k, v]) => ({ id: crypto.randomUUID(), key: k, value: String(v ?? '') }))
    : [newField()];
}

function fieldsToObject(fields: Field[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    if (f.key.trim()) out[f.key.trim()] = f.value;
  }
  return out;
}

// The Transform config *is* the output shape — every root key besides label/alias
// is an output field. Raw mode is therefore just a second view onto those same
// keys, not a separate config key.
function parseConfig(config: Record<string, unknown> | undefined) {
  const entries = outputEntries(config);
  // Nested objects/arrays can't be represented as flat key/value rows, so a
  // config containing them opens directly in raw mode.
  const nested = entries.some(([, v]) => isNested(v));
  return {
    mode: (nested ? 'raw' : 'fields') as Mode,
    fields: nested ? [] : entriesToFields(entries),
    raw: entries.length > 0 ? JSON.stringify(Object.fromEntries(entries), null, 2) : '',
  };
}

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

export function TransformStepConfig({ node, onUpdate }: Props) {
  const [mode, setMode] = useState<Mode>(() => parseConfig(node.config).mode);
  const [fields, setFields] = useState<Field[]>(() => parseConfig(node.config).fields);
  const [raw, setRaw] = useState(() => parseConfig(node.config).raw);
  const [rawError, setRawError] = useState<string | null>(null);
  const [label, setLabel] = useState(() => (node.config?.label as string) ?? '');
  const [alias, setAlias] = useState<string>((node.config?.alias as string) ?? '');
  const [showExamples, setShowExamples] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const p = parseConfig(node.config);
    setMode(p.mode);
    setFields(p.fields);
    setRaw(p.raw);
    setRawError(null);
    setLabel((node.config?.label as string) ?? '');
    setAlias((node.config?.alias as string) ?? '');
  }, [node.id]);

  useEffect(() => {
    let output: Record<string, unknown>;
    if (mode === 'fields') {
      output = fieldsToObject(fields);
    } else if (!raw.trim()) {
      output = {};
    } else {
      // Invalid JSON is not committed — the last valid config stays persisted.
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        setRawError('Invalid JSON');
        return;
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setRawError('Top level must be a JSON object');
        return;
      }
      setRawError(null);
      output = parsed as Record<string, unknown>;
    }

    const next: Record<string, unknown> = {
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(alias.trim() ? { alias: alias.trim() } : {}),
      ...output,
    };
    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fields, raw, label, alias]);

  function updateField(id: string, patch: Partial<Field>) {
    setFields(fs => fs.map(f => f.id === id ? { ...f, ...patch } : f));
  }

  function switchMode(next: Mode) {
    if (next === mode) return;

    if (next === 'raw') {
      const obj = fieldsToObject(fields);
      setRaw(Object.keys(obj).length > 0 ? JSON.stringify(obj, null, 2) : '');
      setRawError(null);
      setMode('raw');
      return;
    }

    if (!raw.trim()) {
      setFields([newField()]);
      setRawError(null);
      setMode('fields');
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      setRawError('Fix the JSON before switching to Fields');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setRawError('Top level must be a JSON object');
      return;
    }
    const entries = Object.entries(parsed as Record<string, unknown>);
    // Refuse rather than silently dropping structure the flat view can't hold.
    if (entries.some(([, v]) => isNested(v))) {
      setRawError('Nested fields can only be edited as raw JSON');
      return;
    }
    setFields(entriesToFields(entries));
    setRawError(null);
    setMode('fields');
  }

  function formatRaw() {
    if (!raw.trim()) return;
    try {
      setRaw(JSON.stringify(JSON.parse(raw), null, 2));
      setRawError(null);
    } catch {
      setRawError('Cannot format: invalid JSON');
    }
  }

  return (
    <div
      className={`flex flex-col gap-2 flex-1 ${
        mode === 'fields' ? 'overflow-hidden' : 'overflow-y-auto'
      }`}
    >
      {/* Label */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Label</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs bg-background"
          placeholder="e.g. Map task fields"
          value={label}
          onChange={e => setLabel(e.target.value)}
        />
      </div>

      {/* Alias */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Step alias</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. map-fields"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
        {alias && (
          <p className="text-xs text-muted-foreground">
            Reference as{' '}
            <span className="font-mono text-primary">{`{{${alias}.field}}`}</span>
          </p>
        )}
      </div>

      {/* Output — Fields / Raw JSON toggle */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-medium">Output</p>
        <div className="flex items-center gap-1.5">
          {mode === 'raw' && (
            <button
              onClick={formatRaw}
              title="Format JSON (Tab indents)"
              className="text-xs text-muted-foreground hover:text-primary px-1.5 py-0.5 rounded border border-transparent hover:border-input transition-colors"
            >
              Format
            </button>
          )}
          <div className="flex rounded border border-input overflow-hidden text-xs">
            {(['fields', 'raw'] as const).map(m => (
              <button
                key={m}
                onClick={() => switchMode(m)}
                className={`px-2 py-0.5 transition-colors ${
                  mode === m
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                {m === 'fields' ? 'Fields' : 'Raw JSON'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {mode === 'fields' ? (
        <>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-1 text-xs text-muted-foreground px-1">
            <span>Output key</span>
            <span>Template expression</span>
            <span />
          </div>

          <div className="space-y-1 overflow-y-auto flex-1">
            {fields.map(f => (
              <div key={f.id} className="grid grid-cols-[1fr_1fr_auto] gap-1 items-center">
                <input
                  type="text"
                  className="border border-input rounded p-1.5 text-xs font-mono bg-background"
                  placeholder="fieldName"
                  value={f.key}
                  onChange={e => updateField(f.id, { key: e.target.value })}
                />
                <input
                  type="text"
                  className="border border-input rounded p-1.5 text-xs font-mono bg-background"
                  placeholder="{{step.field}}"
                  value={f.value}
                  onChange={e => updateField(f.id, { value: e.target.value })}
                />
                <button
                  onClick={() => setFields(fs => fs.filter(r => r.id !== f.id))}
                  className="text-muted-foreground hover:text-destructive px-1 text-sm leading-none"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={() => setFields(fs => [...fs, newField()])}
            className="w-full border border-input text-xs text-muted-foreground rounded py-1 hover:bg-muted"
          >
            + Add field
          </button>

          <p className="text-xs text-muted-foreground">
            Each key becomes a field in the step output. Values support <span className="font-mono">{'{{nodeId.field}}'}</span> templates.
          </p>
        </>
      ) : (
        <>
          <JsonTextarea
            className="w-full border border-input rounded p-1.5 text-xs font-mono bg-background min-h-[160px] resize-y"
            placeholder={'{\n  "title": "{{body.name}}",\n  "assignee": {\n    "id": "{{http-1.body.userId}}"\n  }\n}'}
            value={raw}
            onChange={next => { setRaw(next); setRawError(null); }}
          />
          {rawError && <p className="text-destructive text-xs mt-0.5">{rawError}</p>}
          <p className="text-xs text-muted-foreground">
            The JSON object becomes the step output. String values support{' '}
            <span className="font-mono">{'{{nodeId.field}}'}</span> templates and are coerced to
            numbers, booleans or null when the resolved value is one. Nested objects and arrays are
            resolved too.
          </p>
        </>
      )}

      {/* Expression syntax guide */}
      <div className="border border-input rounded text-xs">
        <button
          onClick={() => setShowExamples(s => !s)}
          className="w-full flex items-center justify-between px-2 py-1.5 text-muted-foreground hover:bg-muted rounded"
        >
          <span className="font-medium">Expression syntax</span>
          <span>{showExamples ? '▲' : '▼'}</span>
        </button>

        {showExamples && (
          <div className="px-2 pb-2 space-y-2 border-t">
            {([
              {
                label: 'Field access',
                expr: '{{http-1.body.id}}',
                desc: "Read a field from a previous step's output",
              },
              {
                label: 'Array index',
                expr: '{{body[0].entity.projectId}}',
                desc: 'Access the first element of an array',
              },
              {
                label: 'Nullish coalescing',
                expr: '{{body.parentId}} ?? {{http-1.body.id}}',
                desc: 'Use the first non-empty value',
              },
              {
                label: 'First array item',
                expr: '{{http-1.body | first | path: id}}',
                desc: 'Get the id of the first array element',
              },
              {
                label: 'Last array item',
                expr: '{{http-1.body | last | path: name}}',
                desc: 'Get the name of the last array element',
              },
              {
                label: 'Find by field',
                expr: '{{http-1.body | find: name == "Done" | path: id}}',
                desc: 'Find the first item where name equals "Done"',
              },
              {
                label: 'Sort ascending',
                expr: '{{http-1.body | orderBy: order | first | path: id}}',
                desc: "Sort by order (asc) and get first item's id",
              },
              {
                label: 'Sort descending',
                expr: '{{http-1.body | orderBy: createdAt desc | first | path: id}}',
                desc: "Sort by createdAt (desc) and get first item's id",
              },
            ] as const).map(({ label, expr, desc }) => (
              <div key={label} className="mt-1.5">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-muted-foreground font-medium">{label}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(expr)}
                    className="text-primary hover:text-primary/80 text-xs"
                    title="Copy"
                  >
                    copy
                  </button>
                </div>
                <code className="block bg-muted rounded px-1.5 py-0.5 font-mono text-foreground break-all mt-0.5">
                  {expr}
                </code>
                <span className="text-muted-foreground">{desc}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
